import { Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { RequestApplicationModel } from 'src/shared/models/requests/request-application.model';
import { RequestFilters } from 'src/shared/models/requests/request-filters.model';
import { RequestModel, RequestStatus, RequestType } from 'src/shared/models/requests/request.model';
import { AuthenticatedHttpService } from './authenticatedHttpService.service';
import { LocalStorageService } from './local-storage.service';
import { defaultRequestsPageSize } from 'src/constants/defaults';
import { Order } from 'src/shared/models/common/order';
import { environment } from 'src/environments/environment';
import { HttpUtils } from 'src/shared/utils/http-utils';
import { CreateRequestApplicationModel } from 'src/shared/models/requests/request-application-create.model';
import { UpdateRequestModel } from 'src/shared/models/requests/request-update.model';
import { RequestMessageModel } from 'src/shared/models/requests/request-message.model';
import * as fp from "lodash/fp";

export const RequestStatusDictionary = {
    [RequestStatus.Approved]: 'checkmark-circle-outline',
    [RequestStatus.Feedback]: 'arrow-redo-circle-outline',
    [RequestStatus.Rejected]: 'close-circle-outline',
    [RequestStatus.Requested]: 'hourglass-outline',
}

export enum RequestApplicationSorting {
    None = 1,
    Approved,
    Rejected,
    Requested,
    Feedback,
    Unread,
}

@Injectable({ providedIn: 'root' })
export class RequestProvider {

    private static readonly APPLICATIONS = 'REQUESTS_APPLICATIONS'

    private readonly _applications = signal<Array<RequestApplicationModel>>([]);
    public readonly applications$ = toObservable(this._applications);
    public readonly applications = this._applications.asReadonly();

    private readonly _sortedApplications = signal<Array<RequestApplicationModel>>([]);
    public readonly sortedApplications$ = toObservable(this._sortedApplications);
    public readonly sortedApplications = this._sortedApplications.asReadonly();

    count: number;
    total: number;
    filter: RequestFilters;

    constructor(
        protected http: AuthenticatedHttpService,
        protected storage: LocalStorageService,
    ) { }

    async fetchApplications() {
        this.filter = { order: Order.Descending, page: 0, pageSize: defaultRequestsPageSize }

        const response = await this.http.wrapIntoOffline<{ total: number, items: Array<RequestApplicationModel> }>({
            fetch: this.http.get,
            params: [`${environment.web.apiUrl}requests/applications?${HttpUtils.toParamsString(this.filter)}`],
            storeKey: RequestProvider.APPLICATIONS,
            renew: !fp.get("page", this.filter)
        });

        const applications = response?.result?.items || new Array<RequestApplicationModel>();

        this.count = applications.length;
        this.total = response.result?.total!;

        this._applications.set(applications.filter(a => a.requests != null));
    }

    async uploadApplications(page: number) {
        if (this._applications().length >= this.total) return;

        this.filter.page = page;

        const response = await this.http.wrapIntoOffline<{ total: number, items: Array<RequestApplicationModel> }>({
            fetch: this.http.get,
            params: [`${environment.web.apiUrl}requests/applications?${HttpUtils.toParamsString(this.filter)}`],
            storeKey: RequestProvider.APPLICATIONS,
            renew: !fp.get("page", this.filter)
        });

        const applications = response?.result?.items || new Array<RequestApplicationModel>();
        const newApplicationList = [...this._applications(), ...applications]

        this._applications.set(newApplicationList.filter(a => a.requests != null));

        this.count = newApplicationList.length;
        this.total = response.result?.total!;
    }

    sortApplications(sortBy: RequestApplicationSorting) {
        if (sortBy === RequestApplicationSorting.None) {
            this._sortedApplications.set(this._applications());
            return;
        }

        let sortedApplications: RequestApplicationModel[] = [];

        if (sortBy === RequestApplicationSorting.Unread) {
            sortedApplications = this._applications().filter(a => !a.isRead);
        }
        else {
            let requestStatus: RequestStatus | undefined;

            if (sortBy === RequestApplicationSorting.Approved) requestStatus = RequestStatus.Approved;
            if (sortBy === RequestApplicationSorting.Rejected) requestStatus = RequestStatus.Rejected;
            if (sortBy === RequestApplicationSorting.Requested) requestStatus = RequestStatus.Requested;
            if (sortBy === RequestApplicationSorting.Feedback) requestStatus = RequestStatus.Feedback;

            if (requestStatus !== undefined) {
                sortedApplications = this._applications().filter(application => {
                    return application.requests
                        .filter(r => !r.fromClient || r.type === RequestType.Request || r.type === RequestType.UpdateRequest)
                        .pop()?.status === requestStatus;
                });
            }
        }

        this._sortedApplications.set(sortedApplications);
    }

    async createApplication(model: CreateRequestApplicationModel) {
        try {
            return await this.http.post(`${environment.web.apiUrl}requests/application`, model);
        }
        catch (error) {
            console.error(error);
        }
    }

    async checkCanRequestBeUpdated(id: string): Promise<boolean | undefined | null> {
        try {
            const canRequestBeUpdated = await HttpUtils.handle<boolean>({ request: this.http.get(`${environment.web.apiUrl}requests/can-be-updated/application/${id}`) });
            return canRequestBeUpdated;
        }
        catch (error) {
            console.error(error);
            return null;
        }
    }

    async getApplication(id: string): Promise<RequestApplicationModel | null> {
        try {
            const application = await HttpUtils.handle<RequestApplicationModel>({ request: this.http.get(`${environment.web.apiUrl}requests/application/${id}`) });

            if (!application?.requests) return null;

            const storedApplications = await this.storage.get(RequestProvider.APPLICATIONS);
            if (storedApplications && storedApplications.items.length) {
                const applications = [application, ...storedApplications.items];
                this._applications.set(applications);

                await this.storage.set(RequestProvider.APPLICATIONS, { total: this.total, items: applications });
            }
            else {
                this._applications.set([application]);
                await this.storage.set(RequestProvider.APPLICATIONS, { total: this.total, items: [application] });
            }

            return application;
        }
        catch (error) {
            console.error(error);
        }

        return null;
    }

    async updateApplication(id: string): Promise<RequestApplicationModel | null> {
        try {
            const application = await HttpUtils.handle<RequestApplicationModel>({
                request: this.http.get(`${environment.web.apiUrl}requests/application/${id}`)
            });

            if (!application?.requests) return null;

            const list = this._applications();
            const index = list.findIndex(a => a.id === id);

            if (index !== -1) {
                const updatedList = [...list];
                updatedList[index] = { ...updatedList[index], ...application };
                this._applications.set(updatedList);
            }

            return application;
        }
        catch (error) {
            console.error(error);
            return null;
        }
    }

    async read(applicationId: string) {
        try {
            await this.http.put(`${environment.web.apiUrl}requests/read/application/${applicationId}`, null);

            this.updateApplicationIsRead(applicationId, true);

        } catch (error) {
            console.error(error);
        }
    }

    updateApplicationIsRead(applicationId: string, isRead: boolean) {
        const list = this._applications();
        const index = list.findIndex(a => a.id === applicationId);

        if (index !== -1) {
            const updatedList = [...list];
            updatedList[index] = { ...updatedList[index], isRead };
            this._applications.set(updatedList);

            // Also update sorted applications if they exist
            if (this._sortedApplications().length > 0) {
                const sortedIndex = this._sortedApplications().findIndex(a => a.id === applicationId);
                if (sortedIndex !== -1) {
                    const updatedSortedList = [...this._sortedApplications()];
                    updatedSortedList[sortedIndex] = { ...updatedSortedList[sortedIndex], isRead };
                    this._sortedApplications.set(updatedSortedList);
                }
            }
        }
    }

    async updateRequest(applicationId: string, model: UpdateRequestModel | null) {
        try {
            const request = await HttpUtils.handle<RequestModel>({ request: this.http.put(`${environment.web.apiUrl}requests/update/application/${applicationId}`, model) });
            return request;
        }
        catch (error) {
            console.error(error);
            return null;
        }
    }

    async sendMessage(applicationId: string, model: RequestMessageModel) {
        try {
            return await this.http.post(`${environment.web.apiUrl}requests/message/application/${applicationId}`, model);
        }
        catch (error) {
            console.error(error);
        }
    }

    findApplication(id: string): RequestApplicationModel | undefined {
        return this._applications().find(a => a.id === id);
    }

    async purge() {
        await this.storage.remove(RequestProvider.APPLICATIONS);
    }
}